import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import WebinarCard from "@/components/WebinarCard";
import { fmtMoney } from "@/lib/format";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Clock, Award, CheckCircle2, PlayCircle, Users } from "lucide-react";

export default function CourseDetail() {
  const { industryOrSlug } = useParams();
  const slug = industryOrSlug;
  const { location } = useApp();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    if (!location) return;
    api.course(slug, location.country).then(setCourse).catch(() => setCourse(false));
    api.logEvent("course.viewed", { slug });
  }, [slug, location]);

  if (course === false) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-500">Course not found.</div>;
  if (!course) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-slate-500">Loading…</div>;

  const sym = course.symbol;
  const buy = () => navigate(`/checkout?type=course&slug=${slug}`);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="course-detail">
      <div className="grid lg:grid-cols-3 gap-10">
        {/* main */}
        <div className="lg:col-span-2">
          <div className="asf-overline mb-3">{course.industry_name}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{course.title}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-slate-600">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-asf-orange" /> {course.duration_hours} hours</span>
            <span className="flex items-center gap-2"><Award className="h-4 w-4 text-asf-orange" /> Certificate</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-asf-orange" /> All levels</span>
          </div>

          <div className="relative rounded-2xl overflow-hidden mt-8 h-72">
            <img src={course.image} alt={course.title} className="h-full w-full object-cover opacity-80" />
            <div className="absolute inset-0 grid place-items-center bg-asf-app/30">
              <PlayCircle className="h-16 w-16 text-white/90" />
            </div>
          </div>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-semibold mb-3">Overview</h2>
            <p className="text-slate-600 leading-relaxed">{course.overview}</p>
          </section>

          <section className="mt-8 grid sm:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display text-xl font-semibold mb-3">Who it's for</h3>
              <ul className="space-y-2">
                {course.who_for.map((w) => <li key={w} className="flex gap-2 text-slate-600 text-sm"><CheckCircle2 className="h-4 w-4 text-asf-orange shrink-0 mt-0.5" /> {w}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold mb-3">What you'll learn</h3>
              <ul className="space-y-2">
                {course.outcomes.map((o) => <li key={o} className="flex gap-2 text-slate-600 text-sm"><CheckCircle2 className="h-4 w-4 text-asf-orange shrink-0 mt-0.5" /> {o}</li>)}
              </ul>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-semibold mb-4">Curriculum</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {course.curriculum.map((s, i) => (
                <AccordionItem key={s.id} value={s.id} className="asf-card !translate-y-0 hover:!translate-y-0 px-5 border-slate-200">
                  <AccordionTrigger className="hover:no-underline" data-testid={`section-${s.id}`}>
                    <span className="text-left"><span className="font-mono text-asf-orange text-xs mr-3">S{i + 1}</span>{s.title}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pl-2">
                      {s.lessons.map((l) => (
                        <li key={l.id} className="flex items-center gap-2 text-sm text-slate-600">
                          <PlayCircle className="h-4 w-4 text-slate-500" /> {l.title}
                          <span className="ml-auto text-xs text-slate-500">{l.duration_min}m {l.preview && <span className="text-asf-orange ml-1">Preview</span>}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-semibold mb-4">FAQ</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {course.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="asf-card !translate-y-0 hover:!translate-y-0 px-5 border-slate-200">
                  <AccordionTrigger className="hover:no-underline text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-500">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* sticky pricing */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6">
              <div className="font-display text-4xl font-extrabold">{fmtMoney(sym, course.price)}</div>
              <p className="text-xs text-slate-500 mt-1">One-time · lifetime access</p>
              <button data-testid="buy-course-btn" onClick={buy} className="btn-primary w-full py-4 mt-5">Buy This Course</button>
              <Link to={`/webinars/${course.industry}`} data-testid="join-webinar-btn" className="btn-secondary w-full py-4 mt-3 inline-block text-center">Join a Webinar</Link>
              <ul className="mt-6 space-y-2 text-sm text-slate-600">
                {[`${course.duration_hours} hours of content`, "Downloadable resources", "Certificate of completion", "Lifetime access"].map((f) => (
                  <li key={f} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-asf-orange shrink-0" /> {f}</li>
                ))}
              </ul>
            </div>

            {course.next_webinar && (
              <div>
                <div className="asf-overline mb-2">Want to learn more first?</div>
                <WebinarCard webinar={course.next_webinar} />
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
